import React, { useMemo } from "react";
import { Badge } from "@shared/ui/badge";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { useParams } from "react-router-dom";
import { useGetPlanDetailByTab, useGetParameter } from "../../../hooks";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { BadgeList } from "@shared/components/BadgeList";

const parseStringList = (val: any): string[] => {
  if (!val) return [];
  const list: string[] = [];
  const rawArray = Array.isArray(val) ? val.flat() : [val];
  rawArray.forEach((item: any) => {
    if (typeof item === "string" && item.trim()) {
      item.split(",").forEach((s) => {
        const trimmed = s.trim();
        if (trimmed) list.push(trimmed);
      });
    } else if (item !== null && item !== undefined && item !== "") {
      list.push(String(item));
    }
  });
  return list;
};

export const Penalties = () => {
  const { id } = useParams();
  const currency = useCurrency();
  const { data: planDetail } = useGetPlanDetailByTab(id, "penalties");
  const { data: globalParamData } = useGetParameter();

  const parameterOptions = useMemo(() => {
    const raw = Array.isArray(globalParamData)
      ? globalParamData
      : globalParamData?.result || globalParamData?.data || [];
    return Array.isArray(raw) ? raw : [];
  }, [globalParamData]);

  const resolveParamName = (rule: any, group?: any) => {
    const groupName = group?.parameterName || group?.parameter_name;
    if (groupName && groupName.toLowerCase() !== "common" && groupName !== "NA")
      return groupName;
    const rawName = rule.parameterName || rule.parameter_name;
    if (rawName && rawName.toLowerCase() !== "common" && rawName !== "NA")
      return rawName;
    const pId = rule.parameterId || rule.parameter_id || rule.parameter;
    if (!pId) return "—";
    const found = parameterOptions.find(
      (p: any) =>
        String(p.id) === String(pId) || String(p.parameter_id) === String(pId),
    );
    return found
      ? found.name || found.parameter_name || String(pId)
      : String(pId);
  };

  const commonColumns: TableColumn<any>[] = [
    {
      key: "penalty",
      header: "Penalty Name",
      width: "w-[40%]",
      render: (_: any, row: any) => (
        <span className="font-medium text-gray-900">{row.penalty || "—"}</span>
      ),
    },
    {
      key: "chargeType",
      header: "Type",
      width: "w-[20%]",
      render: (_: any, row: any) => (
        <Badge
          variant="secondary"
          className="bg-purple-100 text-purple-700 hover:bg-purple-100 font-normal rounded-full px-3 capitalize"
        >
          {row.chargeType || "—"}
        </Badge>
      ),
    },
    {
      key: "charge",
      header: "Amount",
      width: "w-[20%]",
      render: (_: any, row: any) => (
        <Badge
          variant="outline"
          className={`${row.charge?.includes("%") ? "bg-purple-50 text-purple-700 border-purple-200" : "bg-yellow-50 text-yellow-700 border-yellow-200"} rounded-full px-3`}
        >
          {row.charge}
        </Badge>
      ),
    },
    {
      key: "grace",
      header: "Grace (Days)",
      width: "w-[20%]",
      render: (_: any, row: any) => (
        <span className="text-gray-600">{row.grace}</span>
      ),
    },
  ];

  const paramWiseColumns: TableColumn<any>[] = [
    {
      key: "utilityService",
      header: "Utility Service",
      width: "w-[16%]",
      render: (_: any, row: any) => {
        const items = parseStringList(row.utilityServiceRaw);
        return (
          <BadgeList
            items={items}
            count={2}
            badgeClassName="bg-cyan-50 text-cyan-700 border-cyan-200"
            fallback={<span className="text-gray-400 text-xs">All</span>}
          />
        );
      },
    },
    {
      key: "parameter",
      header: "Parameter",
      width: "w-[16%]",
      render: (_: any, row: any) => (
        <span className="font-medium text-gray-800">{row.parameterName}</span>
      ),
    },
    {
      key: "value",
      header: "Applies To",
      width: "w-[18%]",
      render: (_: any, row: any) => {
        const items = parseStringList(row.valueRaw);
        return (
          <BadgeList
            items={items}
            count={2}
            badgeClassName="bg-purple-50 text-purple-700 border-purple-200"
            fallback={<span className="text-gray-400 text-xs">All</span>}
          />
        );
      },
    },
    {
      key: "penalty",
      header: "Penalty Name",
      width: "w-[18%]",
      render: (_: any, row: any) => (
        <span className="font-medium text-gray-900">{row.penalty || "—"}</span>
      ),
    },
    {
      key: "chargeType",
      header: "Type",
      width: "w-[12%]",
      render: (_: any, row: any) => (
        <Badge
          variant="secondary"
          className="bg-purple-100 text-purple-700 hover:bg-purple-100 font-normal rounded-full px-3 capitalize"
        >
          {row.chargeType || "—"}
        </Badge>
      ),
    },
    {
      key: "charge",
      header: "Amount",
      width: "w-[10%]",
      render: (_: any, row: any) => (
        <Badge
          variant="outline"
          className={`${row.charge?.includes("%") ? "bg-purple-50 text-purple-700 border-purple-200" : "bg-yellow-50 text-yellow-700 border-yellow-200"} rounded-full px-3`}
        >
          {row.charge}
        </Badge>
      ),
    },
    {
      key: "grace",
      header: "Grace (Days)",
      width: "w-[10%]",
      render: (_: any, row: any) => (
        <span className="text-gray-600">{row.grace}</span>
      ),
    },
  ];

  const rulesGrouped =
    planDetail?.result?.rulesGrouped || planDetail?.result?.rules_grouped || [];

  const totalRulesCount = rulesGrouped.reduce(
    (acc: number, group: any) => acc + (group.rules?.length || 0),
    0,
  );

  const overallMode = React.useMemo(() => {
    if (rulesGrouped.length > 0) {
      const hasParam = rulesGrouped.some(
        (g: any) =>
          g.parameter_name?.toLowerCase() !== "common" &&
          g.parameterName?.toLowerCase() !== "common" &&
          (g.parameter_id || g.parameterId),
      );
      if (hasParam) return "parameter-wise";
    }
    return "common";
  }, [rulesGrouped]);

  return (
    <div className="space-y-6">
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-base font-semibold text-gray-900">
            Penalty Rules
          </h3>
          <Badge
            variant="outline"
            className="bg-purple-50 text-purple-700 border-purple-200 rounded-full px-2 py-0.5 text-xs capitalize"
          >
            {overallMode}
          </Badge>
          {totalRulesCount > 0 && (
            <Badge
              variant="outline"
              className="bg-yellow-50 text-yellow-700 border-yellow-200 rounded-full px-2 py-0.5 text-xs"
            >
              {totalRulesCount} {totalRulesCount === 1 ? "rule" : "rules"}
            </Badge>
          )}
        </div>
        <p className="text-xs text-gray-500">
          Each rule fires when the consumer's parameters matches at billing time
          — late fees, reconnection charges, NSF, and any other custom rules
          vary by parameter value.
        </p>
      </div>

      <div className="space-y-4">
        {rulesGrouped.length === 0 ? (
          <div className="text-sm text-gray-500 px-2">
            No penalties configured
          </div>
        ) : (
          rulesGrouped.map((group: any, groupIndex: number) => {
            const rules = group.rules || [];
            const paramName =
              group.parameterName || group.parameter_name || "Common";
            const isCommonGroup =
              (!group.parameterId &&
                !group.parameter_id &&
                paramName.toLowerCase() === "common") ||
              rules.every(
                (r: any) =>
                  !r.mode || r.mode === "common" || r.mode === "COMMON",
              );

            const tableData = rules.map((rule: any, idx: number) => {
              const utilRaw = rule.utilityServices || rule.utility_services;
              const valRaw =
                rule.parameterValues || rule.parameter_values || rule.value;
              const rawChargeType =
                rule.chargeType || rule.charge_type || rule.type || "flat";
              const isPercent =
                rawChargeType === "percent" || rawChargeType === "percentage";

              return {
                id: `${groupIndex}-${idx}`,
                utilityServiceRaw: utilRaw,
                valueRaw: valRaw,
                parameterName: resolveParamName(rule, group),
                penalty: rule.penaltyName || rule.penalty_name || "Penalty",
                chargeType: isPercent ? "Percent" : "Flat",
                charge: isPercent
                  ? `${rule.amount}%`
                  : `${currency} ${rule.amount}`,
                grace:
                  rule.graceDays === 0 || rule.grace_days === 0
                    ? "Immediate"
                    : `${rule.graceDays ?? rule.grace_days ?? 0} days`,
              };
            });

            const columnsToUse = isCommonGroup
              ? commonColumns
              : paramWiseColumns;

            if (isCommonGroup) {
              return (
                <div
                  key={groupIndex}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <GenericTable
                    data={tableData}
                    columns={columnsToUse}
                    rowKey="id"
                  />
                </div>
              );
            }

            return (
              <div
                key={groupIndex}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Parameter:
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {paramName}
                  </span>
                  <Badge
                    variant="outline"
                    className="bg-white text-gray-600 border-gray-200 rounded-full px-2 text-xs"
                  >
                    {rules.length} {rules.length === 1 ? "rule" : "rules"}
                  </Badge>
                </div>
                <GenericTable
                  data={tableData}
                  columns={columnsToUse}
                  rowKey="id"
                />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Penalties;
